import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
    MotifErrorMessage,
    MotifModal,
    MotifModalBody,
    MotifModalFooter,
    MotifModalHeader,
    MotifProgressLoader,
} from "@ey-xd/motif-react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import Button from "@/components/Form/Button/Button";
import ControlSelect from "./SelectBox/editFieldSelectBox";
import { renderOptionList } from "../Form/calculationSummaryHelper";
import "./FormModal.scss";
import { useGetRateTierTemplateListByCaseQuery } from "@/services/rateTierTemplate/RateTierTemplateReducer";
import { CANCEL_BUTTON_VARIANT, SAVE_BUTTON_VARIANT } from "@/components/Images/CommonImages";

type Props = {
    isVisible: boolean;
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    caseId: string;
    stepId: string;
    setIsRefetch: Dispatch<SetStateAction<boolean>>;
    onApplyTemplate: (templateId: string, templateName: string) => void;
};

const FormModalRateTierTemplate = ({
    isVisible,
    setIsVisible,
    caseId,
    stepId,
    setIsRefetch,
    onApplyTemplate,
}: Props) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [templateOptions, setTemplateOptions] = useState<any[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    let defaultOption = useMemo(()=>({
        label: t("global.globalActions.optionDefault"),
        value: "",
      }),[t]);
    const { control, handleSubmit, setValue } = useForm({
        defaultValues: {
            template: "",
        },
    });

    const { data: templateList, isLoading: isLoadingTemplateList, refetch: refetchTemplateList, } =
        useGetRateTierTemplateListByCaseQuery(caseId);

        useEffect(() => {
            if (isVisible) {
                refetchTemplateList();
            }
        }, [isVisible, refetchTemplateList]);

    useEffect(() => {
        if (templateList && templateList.length > 0) {
            const options = renderOptionList(templateList, "templateName", "id",defaultOption);
            setTemplateOptions(options);
        }
    }, [templateList,defaultOption]);

    const onSubmit = async (data: any) => {
        if (!data.template) {
            setErrorMessage("pages.distributions.rateTire.validationMessage.mainMessage");
            return;
        }
        setIsLoading(true);
        try {
            const selectedTemplate = templateOptions.find(
                (option) => option.value === data.template
            );

            if (!selectedTemplate) {
                setErrorMessage("pages.distributions.rateTire.validationMessage.templatNotFound");
                return;
            }

            onApplyTemplate(data.template, selectedTemplate.label);

            setIsRefetch(true);
            setIsVisible(false);
        } catch (error) {
            setErrorMessage(
                "pages.distributions.rateTire.validationMessage.unExpectedError"
            );
        } finally {
            setIsLoading(false);
        }
    };


    const cancelHandler = () => {
        setValue("template", "");
        setErrorMessage("");
        setIsVisible(false);
    };

    return (
        <MotifModal
            onClose={cancelHandler}
            show={isVisible}
            focusTrapOptions={{
                tabbableOptions: {
                    displayCheck: "none",
                },
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="formModalWrapper">
                    <MotifModalHeader>
                        <span className="discard_heading">{t("pages.distributions.rateTire.createTemplate")}</span>
                    </MotifModalHeader>
                    <MotifModalBody>
                        {isLoadingTemplateList ? (
                            <MotifProgressLoader show variant="default" />
                        ) : (
                            <div className="formModal">
                                <div className="motif-row mar-t-20">
                                    <div className="motif-col-md-5">
                                        {isLoadingTemplateList ? (
                                            <MotifProgressLoader show variant="default" />
                                        ) : templateOptions.length === 0 ? (
                                            <p>{t("pages.distributions.rateTire.noTemplete")}</p>
                                        ) : (
                                            <ControlSelect
                                                name={"template"}
                                                //@ts-ignore
                                                control={control}
                                                required={true}
                                                showLabel={true}
                                                label={"pages.distributions.rateTire.selectRateTire"}
                                                placeholder={t("global.globalActions.optionDefault")}
                                                options={templateOptions}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {errorMessage && (
                            <div className="error-container">
                                <MotifErrorMessage>{t(errorMessage)}</MotifErrorMessage>
                            </div>
                        )}
                    </MotifModalBody>
                    <MotifModalFooter>
                        <div className="button-container-add">
                            <Button
                                label={t("global.globalActions.save")}
                                disabled={isLoading}
                                loading={isLoading}
                                variant={SAVE_BUTTON_VARIANT}
                            />
                            <Button
                                label={t("global.globalActions.cancel")}
                                type="button"
                                variant={CANCEL_BUTTON_VARIANT}
                                className="cancelBtn"
                                onClickHandler={cancelHandler}
                            />
                        </div>
                    </MotifModalFooter>
                </div>
            </form>
        </MotifModal>
    );
};

export default FormModalRateTierTemplate;
